import React, { useEffect, useRef, useState } from 'react';
import { useSessionStore } from '../../stores/sessionStore';

export function TimerBar() {
  const phase = useSessionStore(s => s.phase);
  const continueAfterRangeUpdate = useSessionStore(s => s.continueAfterRangeUpdate);

  const [enabled, setEnabled] = useState(false);
  const [totalSeconds, setTotalSeconds] = useState(30);
  const [remaining, setRemaining] = useState(30);
  const [expired, setExpired] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Read settings from localStorage
  useEffect(() => {
    try {
      const on = localStorage.getItem('rr:timer-enabled') === '1';
      const secs = parseInt(localStorage.getItem('rr:timer-seconds') ?? '30', 10);
      setEnabled(on);
      setTotalSeconds(secs);
    } catch { /* ignore */ }
  }, []);

  // Reset and start timer when entering AWAITING_RANGE_UPDATE
  useEffect(() => {
    if (!enabled) return;

    if (phase === 'AWAITING_RANGE_UPDATE') {
      setRemaining(totalSeconds);
      setExpired(false);

      intervalRef.current = setInterval(() => {
        setRemaining(prev => {
          if (prev <= 1) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            setExpired(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      // Clear timer when leaving the phase
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [phase, enabled, totalSeconds]);

  // Auto-advance when timer expires
  useEffect(() => {
    if (expired && phase === 'AWAITING_RANGE_UPDATE') {
      continueAfterRangeUpdate();
    }
  }, [expired, phase, continueAfterRangeUpdate]);

  if (!enabled || phase !== 'AWAITING_RANGE_UPDATE') return null;

  const pct = totalSeconds > 0 ? (remaining / totalSeconds) * 100 : 0;
  const urgent = remaining <= 5;

  return (
    <div className="timer-bar-wrapper">
      <div className="timer-bar-track">
        <div
          className={`timer-bar-fill ${urgent ? 'timer-urgent' : ''}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={`timer-bar-label ${urgent ? 'timer-urgent' : ''}`}>
        {remaining}s
      </span>
    </div>
  );
}
